import React, { useState } from 'react';
import { Play, Radio, Archive } from 'lucide-react';
import {
  LANDING_COCKPIT_CAPTION,
  LANDING_PRODUCT_MOCK_ID,
  LANDING_SECTION_PRODUCT_MOCK,
} from '../../landing/landingCopy';
import {
  LANDING_FEATURED_REPLAY_ALT,
  LANDING_FEATURED_REPLAY_BADGE,
  LANDING_FEATURED_REPLAY_CTA,
  LANDING_FEATURED_REPLAY_MATCHUP,
  LANDING_FEATURED_REPLAY_META,
  LANDING_FEATURED_REPLAY_SUPPORT,
  LANDING_FEATURED_SUPPORT_CARDS,
  LANDING_FEATURED_VALUE_CHIPS,
  landingFeaturedReplayAssetUrl,
} from '../../landing/landingMarketingAssets';

const SUPPORT_CARD_ICONS = [Radio, Play, Archive] as const;

const LscLandingProductMock: React.FC = () => {
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <section
      id={LANDING_PRODUCT_MOCK_ID}
      className="space-y-6 scroll-mt-24"
      data-testid="lsc-landing-product-mock"
    >
      <div className="text-center space-y-2">
        <h2 className="text-2xl md:text-3xl font-bold text-white">{LANDING_SECTION_PRODUCT_MOCK}</h2>
        <p className="text-secondary-400 max-w-2xl mx-auto text-sm md:text-base">
          A real product preview from the Stream Center pilot — focused on the replay outcome members
          actually watch.
        </p>
      </div>

      <div className="relative">
        <div
          className="absolute -inset-4 rounded-3xl bg-primary-600/10 blur-3xl pointer-events-none"
          aria-hidden
        />
        <div
          className="relative space-y-5 rounded-2xl border border-primary-700/30 bg-gradient-to-b from-secondary-800/90 via-secondary-900/95 to-secondary-950 p-4 sm:p-6 shadow-2xl shadow-primary-950/20"
          data-testid="lsc-landing-featured-replay-showcase"
        >
          <article
            className="group relative overflow-hidden rounded-2xl border border-secondary-700/80 bg-secondary-950 shadow-xl transition duration-200 hover:border-primary-700/40 hover:shadow-primary-950/30"
            data-testid="lsc-landing-featured-replay-card"
          >
            <div className="relative aspect-[16/10] sm:aspect-[16/9] overflow-hidden">
              {imageFailed ? (
                <div
                  className="flex h-full items-center justify-center bg-gradient-to-br from-secondary-900 via-primary-950/40 to-secondary-950 p-6"
                  data-testid="lsc-landing-featured-replay-fallback"
                >
                  <p className="text-sm text-secondary-400">Featured replay preview</p>
                </div>
              ) : (
                <img
                  src={landingFeaturedReplayAssetUrl()}
                  alt={LANDING_FEATURED_REPLAY_ALT}
                  loading="eager"
                  decoding="async"
                  className="h-full w-full object-cover object-top transition duration-300 group-hover:scale-[1.01]"
                  data-testid="lsc-landing-featured-replay-image"
                  onError={() => setImageFailed(true)}
                />
              )}
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-secondary-950 via-secondary-950/20 to-transparent"
                aria-hidden
              />
              <div className="absolute inset-x-0 bottom-0 space-y-3 p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="rounded-full border border-primary-600/50 bg-primary-950/70 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-100"
                    data-testid="lsc-landing-featured-replay-badge"
                  >
                    {LANDING_FEATURED_REPLAY_BADGE}
                  </span>
                </div>
                <div className="space-y-1">
                  <h3
                    className="text-xl sm:text-2xl font-bold text-white"
                    data-testid="lsc-landing-featured-replay-matchup"
                  >
                    {LANDING_FEATURED_REPLAY_MATCHUP}
                  </h3>
                  <p
                    className="text-xs sm:text-sm text-secondary-300"
                    data-testid="lsc-landing-featured-replay-meta"
                  >
                    {LANDING_FEATURED_REPLAY_META}
                  </p>
                </div>
                <span
                  className="inline-flex items-center gap-2 rounded-lg border border-primary-700/50 bg-primary-900/50 px-3 py-1.5 text-xs font-semibold text-primary-100 shadow-lg shadow-primary-950/30"
                  data-testid="lsc-landing-featured-replay-cta"
                >
                  <Play className="h-3.5 w-3.5" aria-hidden />
                  {LANDING_FEATURED_REPLAY_CTA}
                </span>
              </div>
            </div>
          </article>

          <p className="text-sm text-secondary-400 text-center max-w-2xl mx-auto">
            {LANDING_FEATURED_REPLAY_SUPPORT}
          </p>

          <div
            className="flex flex-wrap justify-center gap-2"
            data-testid="lsc-landing-featured-value-chips"
          >
            {LANDING_FEATURED_VALUE_CHIPS.map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-secondary-700/80 bg-secondary-900/70 px-3 py-1 text-[11px] font-medium text-secondary-200"
                data-testid="lsc-landing-featured-value-chip"
              >
                {chip}
              </span>
            ))}
          </div>

          <div
            className="grid gap-3 sm:grid-cols-3"
            data-testid="lsc-landing-featured-support-cards"
          >
            {LANDING_FEATURED_SUPPORT_CARDS.map(({ title, description }, index) => {
              const Icon = SUPPORT_CARD_ICONS[index] ?? Archive;
              return (
                <article
                  key={title}
                  className="rounded-xl border border-secondary-700/80 bg-secondary-900/60 p-4 space-y-2 transition duration-200 hover:-translate-y-0.5 hover:border-primary-700/30"
                  data-testid="lsc-landing-featured-support-card"
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-900/40 text-primary-300">
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <h4 className="text-sm font-semibold text-white">{title}</h4>
                  </div>
                  <p className="text-xs text-secondary-400 leading-relaxed">{description}</p>
                </article>
              );
            })}
          </div>

          <p
            className="text-[11px] text-center text-secondary-500"
            data-testid="lsc-landing-cockpit-caption"
          >
            {LANDING_COCKPIT_CAPTION}
          </p>
        </div>
      </div>
    </section>
  );
};

export default LscLandingProductMock;
